import React, { useCallback } from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { Container, Flex, Text, Box } from 'roo-ui/components';
import { themeGet } from 'styled-system';

import { useSelector } from 'react-redux';
import { qantasBusinessRewards, qantasQff } from 'roo-ui/logos';
import { DisclaimerItemLogo, DisclaimerText, DisclaimerLink } from 'components/Footer/DisclaimerItems';
import { useDataLayer } from 'hooks/useDataLayer';
import { getCampaignTermsAndConditions } from 'store/campaign/campaignSelectors';
import { TRIP_ADVISOR_IMG_BRANDING_URL, QCOM_TERMS_AND_CONDITIONS_URL } from 'config';

const TripAdvisorLogo = styled.img`
  width: ${rem('120px')};
  height: ${rem('24px')};
  margin-left: ${themeGet('space.2')};
  margin-bottom: -${themeGet('space.1')};
`;

const Disclaimer = () => {
  const { emitInteractionEvent } = useDataLayer();
  const campaignTermsAndConditions = useSelector(getCampaignTermsAndConditions);
  const defaultCampaignTermsAndConditions =
    'Qantas Frequent Flyer members will earn 3 Qantas Points per A$1 value for hotel stays booked through qantas.com/hotels, except Classic Hotel Rewards. ';
  const pointsClubTermsAndConditions =
    'Points Club members will earn 25% more Qantas Points, and Points Club Plus members will earn 50% more Qantas Points, on hotel stays booked through qantas.com/hotels excluding Qantas Holidays bookings, Airbnb bookings and Bonus Points awarded upon receipt of a Qantas Hotels cancellation credit voucher. Qantas Points will not be earned on cancelled or refunded bookings. Qantas Points cannot be split between two or more members occupying the same room. To earn Qantas Points, quote your Qantas Frequent Flyer membership number when booking. Members will not be able to redeem points for, or earn points on additional charges paid to the accommodation provider for extras (including cots, breakfasts and other incidentals) on check-in or check-out (as applicable). Qantas Points will be credited to your account at least 8 weeks after check-out';
  const payableAtPropertyTermsAndConditions = 'Qantas Points are not earned on any amounts payable directly to the hotel.';

  const termsAndConditions = campaignTermsAndConditions || defaultCampaignTermsAndConditions;

  const handleOnClickFrequentFlyerLink = useCallback(() => {
    emitInteractionEvent({ type: 'Frequent Flyer Conditions', value: 'Conditions Apply Link Selected' });
  }, [emitInteractionEvent]);

  const handleOnClickBusinessRewardLink = useCallback(() => {
    emitInteractionEvent({ type: 'Business Rewards Conditions', value: 'Conditions Apply Link Selected' });
  }, [emitInteractionEvent]);

  return (
    <Container data-print-style="hidden">
      <Text display="block" fontSize="base" fontWeight="bold" mb={3} mt={5}>
        Important information
      </Text>

      <Flex flexDirection={['column', 'row']} alignItems="flex-start">
        <DisclaimerItemLogo src={qantasQff} alt="Qantas QFF Logo" role="img" aria-label="Qantas QFF Logo" />
        <DisclaimerText data-testid="points-disclaimer-text">
          ^ {termsAndConditions} &nbsp;
          <Box mt={2} data-testid="points-club-t&c">
            {pointsClubTermsAndConditions} &nbsp;
          </Box>
          {payableAtPropertyTermsAndConditions} &nbsp;
          <DisclaimerLink data-testid="QFFconditionApplyLink" href={QCOM_TERMS_AND_CONDITIONS_URL} onClick={handleOnClickFrequentFlyerLink}>
            Conditions apply
          </DisclaimerLink>
        </DisclaimerText>
      </Flex>

      <Flex flexDirection={['column', 'row']} alignItems="flex-start">
        <DisclaimerItemLogo
          src={qantasBusinessRewards}
          alt="Qantas Business Reward Logo"
          role="img"
          aria-label="Qantas Business Reward Logo"
        />
        <DisclaimerText>
          * Business earn 1 Qantas Point per AU$1 spent on eligible Qantas Hotels worldwide. Eligible Qantas hotels excludes Classic Hotel
          Rewards. A business must be a Qantas Business Rewards Member to earn Qantas Points for your business. Qantas Points are not earned
          on any amounts payable directly to the hotel. &nbsp;
          <DisclaimerLink
            data-testid="businessRewardConditionApplyLink"
            href={QCOM_TERMS_AND_CONDITIONS_URL}
            onClick={handleOnClickBusinessRewardLink}
          >
            Conditions apply
          </DisclaimerLink>
        </DisclaimerText>
      </Flex>

      <Flex>
        <DisclaimerText>
          ~ Saving is off the hotel&#39;s generally available rate for the same property, room type, days, inclusions and conditions
        </DisclaimerText>
      </Flex>
      <Flex>
        <DisclaimerText>
          &dagger; Includes fee payable in local currency direct to hotel. Prices in AUD are approx and based on today&#39;s exchange rate.
        </DisclaimerText>
      </Flex>
      <Flex mb={[1, 6, 6]}>
        <DisclaimerText display="flex">
          Hotel ratings and reviews provided by
          <TripAdvisorLogo src={TRIP_ADVISOR_IMG_BRANDING_URL} alt="Trip-Advisor Logo" role="img" aria-label="Trip-Advisor Logo" />
        </DisclaimerText>
      </Flex>
    </Container>
  );
};

export default Disclaimer;
